import * as React from 'react'
import { Box, type BoxProps } from '../../components/Box'
import { sx as sxFunc, type SxProps } from '../../components/sx'
import clsx from 'clsx'
import styles from './index.module.scss'

export interface LayoutContainerClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the header element. */
  header: string
  /** Styles applied to the content element. */
  content: string
  /** Styles applied to the footer element. */
  footer: string
}
export interface LayoutContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<LayoutContainerClasses>
  /**
   * Header is content layout component that allows you to set content on top of the platform layout.
   */
  header?: JSX.Element
  /**
   * Footer is content layout component that allows you to set content on bottom of the platform layout.
   */
  footer?: JSX.Element
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * @default {}
   */
  slotProps?: {
    /** Header Box provide box props to header */
    Header?: BoxProps
    /** Content Box provide box props to content */
    Content?: BoxProps
    /** Footer Box provide box props to footer */
    Footer?: BoxProps
  }
  /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `components` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slots?: {
    /** Set any other type of component in place of 'Box'
     *
     * @default Box
     */
    Header: React.ElementType
    /** Set any other type of component in place of 'Box'
     *
     * @default Box
     */
    Content: React.ElementType
    /** Set any other type of component in place of 'Box'
     *
     * @default Box
     */
    Footer: React.ElementType
  }
}
export const LayoutContainer = React.forwardRef<
  HTMLDivElement,
  LayoutContainerProps
>(function LayoutContainer(props, ref) {
  const {
    className,
    classes,
    children,
    Component = 'div',
    header,
    footer,
    sx,
    slotProps = {},
    slots = {
      Header: Box,
      Content: Box,
      Footer: Box,
    },
    ...other
  } = props

  return (
    <Component
      className={clsx(styles.root, classes?.root, className, sxFunc(sx))}
      ref={ref}
      {...other}
    >
      {header && (
        <slots.Header
          {...slotProps?.Header}
          className={clsx(
            styles.header,
            classes?.header,
            slotProps?.Header?.className
          )}
        >
          {header}
        </slots.Header>
      )}
      <slots.Content
        {...slotProps?.Content}
        className={clsx(
          styles.content,
          classes?.content,
          slotProps?.Content?.className
        )}
      >
        {children}
      </slots.Content>
      {footer && (
        <slots.Footer
          {...slotProps?.Footer}
          className={clsx(
            styles.footer,
            classes?.footer,
            slotProps?.Footer?.className
          )}
        >
          {footer}
        </slots.Footer>
      )}
    </Component>
  )
})
